import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import AccountSearch from '../../../../components/custom-input/AccountSearch';
import DateRangeInput from '../../../../components/custom-input/DateInputRange';
import { FilterDropdown, Loader } from '../../../../components';
import PagedTable, { type PagedTableHeader } from '../../../../components/sorted-table/PagedTable';
import {
  downloadBreederPaymentHistoryReport,
  getMarketplaceBreederPaymentTransactions,
  markBreederPaymentTransactionAsPaid,
} from '../../../../api/MarketPlaceBreederPaymentTransactionApi';
import { showToast } from '../../../../services/toast.service';
import { toastMessages } from '../../../../constants/errorMessages';
import { BREEDER_PAYMENTS_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../../constants/common';
import type { ODataParams, StandardParams } from '../../../../types/interfaces/apiParams.interfaces';
import type { Account } from '../../../../types/interfaces/account.interfaces';
import type { MarketPlaceBreederPaymentTransaction } from '../../../../types/interfaces/marketPlace.interfaces';
import { PaymentStatus } from '../../../../types/enums';
import { centsToDollars } from '../../../../services/account.service';
import { dateFormatMMDDYYYY } from '../../../../utils/commonUtils';
import MarkAsPaidModal from './MarkAsPaidModal';
import PaymentHistoryActionControl from './PaymentHistoryActionControl';
import BreederPaymentDetailsModal from './BreederPaymentDetailsModal';
import { DownloadSVG } from '../../../../components/svgs';
import './paymentHistory.scss';

const PAYMENT_HISTORY_PAGE_SIZE = 10;
const PAYMENT_HISTORY_INCLUDE = 'Account';
const BILLED_DATE_FILTER_FIELD = 'billedDate';
const PAYMENT_HISTORY_REPORT_NAME = 'Breeder_Payment_History';

const PAYMENT_STATUS_OPTIONS = [
  { name: 'All', value: '' },
  { name: 'Pending', value: PaymentStatus.Pending },
  { name: 'Successful', value: PaymentStatus.Successful },
  { name: 'Failed', value: PaymentStatus.Failed },
  { name: 'Not Attempted', value: PaymentStatus.NotAttempted },
];

interface BreederPaymentHistoryRow {
  id: string;
  breederPaymentTransactionId: number;
  outstandingBalanceCents: number | null;
  transactionReferenceId: string;
  breederName: string;
  orders: number;
  grossAmountCents: number;
  commissionCents: number;
  netPayableCents: number;
  paidDate: string;
  status: string;
}

const HISTORY_PAGED_HEADERS: PagedTableHeader[] = [
  { displayName: BREEDER_PAYMENTS_CONSTANTS.TRANSACTION_REFERENCE_ID },
  { displayName: TABLE_HEADER_CONSTANTS.BREEDER_NAME },
  { displayName: TABLE_HEADER_CONSTANTS.ORDERS },
  { displayName: TABLE_HEADER_CONSTANTS.GROSS_AMOUNT },
  { displayName: TABLE_HEADER_CONSTANTS.COMMISSION },
  { displayName: TABLE_HEADER_CONSTANTS.NET_PAYABLE },
  { displayName: BREEDER_PAYMENTS_CONSTANTS.PAID_DATE },
  { displayName: TABLE_HEADER_CONSTANTS.STATUS },
  { displayName: TABLE_HEADER_CONSTANTS.ACTION },
];

const formatUsdFromCents = (amountCents?: number): string => `$${centsToDollars(amountCents ?? 0)}`;

const mapBreederPaymentToRow = (item: MarketPlaceBreederPaymentTransaction): BreederPaymentHistoryRow => ({
  id: String(item.breederPaymentTransactionId),
  breederPaymentTransactionId: item.breederPaymentTransactionId,
  outstandingBalanceCents: item.outstandingBalanceCents ?? null,
  transactionReferenceId: item.transactionReferenceId?.trim() || String(item.breederPaymentTransactionId ?? ''),
  breederName: item.account?.name ?? '',
  orders: item.numberOfOrders ?? 0,
  grossAmountCents: item.totalAmountCents ?? 0,
  commissionCents: item.rsgCommissionCents ?? 0,
  netPayableCents: item.paidAmountCents ?? 0,
  paidDate: item.billedDate ? dateFormatMMDDYYYY(new Date(item.billedDate)) : '',
  status: item.paymentStatus ?? '',
});

const canMarkTransactionAsPaid = (row: BreederPaymentHistoryRow): boolean => {
  const status = row.status.trim().toLowerCase();
  return (
    (status === PaymentStatus.Failed.toLowerCase() || status === PaymentStatus.NotAttempted.toLowerCase()) &&
    (row.outstandingBalanceCents ?? 0) > 0
  );
};

const getPaymentStatusLabel = (row: BreederPaymentHistoryRow): string => {
  const isMarkAsPaidEligible = canMarkTransactionAsPaid(row);

  if (row.status === PaymentStatus.NotAttempted && !isMarkAsPaidEligible) {
    return BREEDER_PAYMENTS_CONSTANTS.NOT_ATTEMPTED_MARKED_AS_PAID;
  }

  return PAYMENT_STATUS_OPTIONS.find(item => item.value === row.status)?.name ?? row.status;
};

const normalizeBreederPayments = (payload: unknown): MarketPlaceBreederPaymentTransaction[] => {
  if (Array.isArray(payload)) return payload as MarketPlaceBreederPaymentTransaction[];
  if (payload && typeof payload === 'object' && 'result' in payload) {
    const result = (payload as { result?: unknown }).result;
    return Array.isArray(result) ? (result as MarketPlaceBreederPaymentTransaction[]) : [];
  }
  return [];
};

interface PaymentHistoryCardProps {
  row: BreederPaymentHistoryRow;
  onOpenMarkAsPaid: (row: BreederPaymentHistoryRow) => void;
  onViewDetails: (row: BreederPaymentHistoryRow) => void;
}

const PaymentHistoryCard = ({ row, onOpenMarkAsPaid, onViewDetails }: PaymentHistoryCardProps): JSX.Element => {
  const isEnabled = canMarkTransactionAsPaid(row);
  const actionControl = (
    <PaymentHistoryActionControl
      isEnabled={isEnabled}
      onMarkAsPaid={() => onOpenMarkAsPaid(row)}
      onViewDetails={() => onViewDetails(row)}
    />
  );

  return (
    <div className="card max-width breeder-payments__card">
      <div className="breeder-payments-card payment-history-card">
        <div className="breeder-payments-card__content">
          <div className="breeder-payments-card__grid">
            <div className="card-content-section">
              <div className="left column">
                <span className="breeder-payments-card__label">{BREEDER_PAYMENTS_CONSTANTS.TRANSACTION_REFERENCE_ID}</span>
                <span>{row.transactionReferenceId}</span>
              </div>
              <div className="left column">
                <span className="breeder-payments-card__label">{TABLE_HEADER_CONSTANTS.BREEDER_NAME}</span>
                <span>{row.breederName}</span>
              </div>
              <div className="right column">
                <span className="breeder-payments-card__label">{TABLE_HEADER_CONSTANTS.ORDERS}</span>
                <span>{row.orders}</span>
              </div>
            </div>
            <div className="card-content-section">
              <div className="left column">
                <span className="breeder-payments-card__label">{TABLE_HEADER_CONSTANTS.GROSS_AMOUNT}</span>
                <span>{formatUsdFromCents(row.grossAmountCents)}</span>
              </div>
              <div className="left column">
                <span className="breeder-payments-card__label">{TABLE_HEADER_CONSTANTS.COMMISSION}</span>
                <span>{formatUsdFromCents(row.commissionCents)}</span>
              </div>
              <div className="right column">
                <span className="breeder-payments-card__label">{TABLE_HEADER_CONSTANTS.NET_PAYABLE}</span>
                <span>{formatUsdFromCents(row.netPayableCents)}</span>
              </div>
            </div>
            <div className="card-content-section">
              <div className="left column">
                <span className="breeder-payments-card__label">{BREEDER_PAYMENTS_CONSTANTS.PAID_DATE}</span>
                <span>{row.paidDate}</span>
              </div>
              <div className="right column">
                <span className="breeder-payments-card__label">{TABLE_HEADER_CONSTANTS.STATUS}</span>
                <span>{getPaymentStatusLabel(row)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="payment-history-card__action-rail">
          <span className="payment-history-card__action-control">{actionControl}</span>
        </div>
      </div>
    </div>
  );
};

const PaymentHistory = (): JSX.Element => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [entitySearchAccount, setEntitySearchAccount] = useState<Account | undefined>(undefined);
  const [paymentDateRange, setPaymentDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<BreederPaymentHistoryRow[]>([]);
  const [mobileOffset, setMobileOffset] = useState(0);
  const [tableRefresh, setTableRefresh] = useState(0);
  const [selectedRowForMarkAsPaid, setSelectedRowForMarkAsPaid] = useState<BreederPaymentHistoryRow | null>(null);
  const [selectedRowForDetails, setSelectedRowForDetails] = useState<BreederPaymentHistoryRow | null>(null);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const mobileCallNumber = useRef(0);
  const hasAllMobileResults: MutableRefObject<boolean> = useRef(false);
  const isLoadingAdditionalMobile: MutableRefObject<boolean> = useRef(false);
  const mobileOffsetRef = useRef(0);
  const inFlightPagedRequests = useRef<Partial<Record<string, Promise<any>>>>({});

  useEffect(() => {
    mobileOffsetRef.current = mobileOffset;
  }, [mobileOffset]);

  const paymentHistoryFilter = useMemo((): string | undefined => {
    const parts: string[] = [];
    if (entitySearchAccount?.accountId != null) {
      parts.push(`accountId eq ${entitySearchAccount.accountId}`);
    }

    if (selectedStatus) {
      parts.push(`paymentStatus eq ${selectedStatus}`);
    }

    const { startDate, endDate } = paymentDateRange;
    const rangeStart = startDate ?? endDate;
    const rangeEnd = endDate ?? startDate;
    if (rangeStart && rangeEnd) {
      const start = new Date(rangeStart);
      start.setHours(0, 0, 0, 0);
      const endInclusive = new Date(rangeEnd);
      endInclusive.setHours(23, 59, 59, 999);
      parts.push(
        `${BILLED_DATE_FILTER_FIELD} gte ${start.toISOString()};${BILLED_DATE_FILTER_FIELD} lte ${endInclusive.toISOString()}`,
      );
    }

    return parts.length > 0 ? parts.join(';') : undefined;
  }, [entitySearchAccount?.accountId, paymentDateRange, selectedStatus]);

  const loadMobileData = useCallback(
    async (offsetOverride?: number) => {
      if (accountId == null) return;
      if (globalThis.innerWidth > 767) return;
      const offset = offsetOverride ?? mobileOffsetRef.current;
      if (offset > 0 && hasAllMobileResults.current) return;

      mobileCallNumber.current++;
      const callNum = mobileCallNumber.current;

      try {
        setLoading(true);
        const standard: StandardParams = {
          include: PAYMENT_HISTORY_INCLUDE,
          filter: paymentHistoryFilter,
          limit: PAYMENT_HISTORY_PAGE_SIZE,
          offset,
        };
        const { data: response } = await getMarketplaceBreederPaymentTransactions(standard);
        if (callNum !== mobileCallNumber.current) return;
        const data = normalizeBreederPayments(response);
        const mappedRows = data.map(mapBreederPaymentToRow);
        setRows(prev => (offset === 0 ? mappedRows : [...prev, ...mappedRows]));
        hasAllMobileResults.current = data.length === 0 || data.length < PAYMENT_HISTORY_PAGE_SIZE;
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        if (offset === 0) {
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [accountId, paymentHistoryFilter],
  );

  const loadMoreMobile = useCallback(() => {
    setMobileOffset(prev => prev + PAYMENT_HISTORY_PAGE_SIZE);
  }, []);

  const handleMobileScroll = useCallback(() => {
    if (globalThis.innerWidth > 767) return;
    const docEl = document.documentElement;
    const body = document.body;
    const scrollTop = globalThis.pageYOffset ?? docEl.scrollTop ?? body.scrollTop ?? 0;
    const scrollHeight = Math.max(docEl.scrollHeight, body.scrollHeight);
    const clientHeight = docEl.clientHeight || globalThis.innerHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom <= clientHeight;
    if (atBottom && !isLoadingAdditionalMobile.current && !hasAllMobileResults.current) {
      isLoadingAdditionalMobile.current = true;
      loadMoreMobile();
    }
  }, [loadMoreMobile]);

  useEffect(() => {
    if (accountId == null) return;
    hasAllMobileResults.current = false;
    setRows([]);
    setMobileOffset(0);
    globalThis.innerWidth <= 767 && void loadMobileData(0);
  }, [accountId, tableRefresh, loadMobileData]);

  useEffect(() => {
    if (globalThis.innerWidth > 767) return;
    if (mobileOffset === 0) return;
    void loadMobileData();
  }, [mobileOffset, loadMobileData]);

  useEffect(() => {
    isLoadingAdditionalMobile.current = false;
  }, [rows]);

  useEffect(() => {
    const opts: AddEventListenerOptions = { passive: true };
    globalThis.addEventListener('scroll', handleMobileScroll, opts);
    globalThis.addEventListener('touchmove', handleMobileScroll, opts);
    return () => {
      globalThis.removeEventListener('scroll', handleMobileScroll, opts);
      globalThis.removeEventListener('touchmove', handleMobileScroll, opts);
    };
  }, [handleMobileScroll]);

  const getPaymentHistoryPaged = useCallback(
    (params: StandardParams) => {
      const requestKey = JSON.stringify({
        include: PAYMENT_HISTORY_INCLUDE,
        filter: paymentHistoryFilter,
        limit: params.limit,
        offset: params.offset,
        sort: params.sort,
      });

      const inFlightRequest = inFlightPagedRequests.current[requestKey];
      if (inFlightRequest) {
        return inFlightRequest;
      }

      const standard: StandardParams = {
        include: PAYMENT_HISTORY_INCLUDE,
        filter: paymentHistoryFilter,
        limit: params.limit,
        offset: params.offset,
        sort: params.sort,
      };
      const request = getMarketplaceBreederPaymentTransactions(standard)
        .then(response => {
          const transactions = normalizeBreederPayments(response.data);
          const mappedRows = transactions.map(mapBreederPaymentToRow);
          return { ...response, data: mappedRows };
        })
        .finally(() => {
          delete inFlightPagedRequests.current[requestKey];
        });

      inFlightPagedRequests.current[requestKey] = request;
      return request;
    },
    [paymentHistoryFilter],
  );

  useEffect(() => {
    if (accountId == null) return;
    setTableRefresh(previous => previous + 1);
  }, [accountId, paymentHistoryFilter]);

  const closeMarkAsPaidModal = useCallback(() => {
    if (markingAsPaid) return;
    setSelectedRowForMarkAsPaid(null);
  }, [markingAsPaid]);

  const handleOpenMarkAsPaidModal = useCallback((row: BreederPaymentHistoryRow) => {
    if (!canMarkTransactionAsPaid(row)) return;
    setSelectedRowForMarkAsPaid(row);
  }, []);

  const handleOpenDetailsModal = useCallback((row: BreederPaymentHistoryRow) => {
    setSelectedRowForDetails(row);
  }, []);

  const handleConfirmMarkAsPaid = useCallback(async () => {
    if (!selectedRowForMarkAsPaid || markingAsPaid) return;
    try {
      setMarkingAsPaid(true);
      await markBreederPaymentTransactionAsPaid(selectedRowForMarkAsPaid.breederPaymentTransactionId);
      showToast.success(toastMessages.MARK_AS_PAID_SUCCESS);
      setSelectedRowForMarkAsPaid(null);
      setTableRefresh(previous => previous + 1);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setMarkingAsPaid(false);
    }
  }, [markingAsPaid, selectedRowForMarkAsPaid]);

  const renderActionControl = useCallback(
    (row: BreederPaymentHistoryRow): JSX.Element => {
      const isEnabled = canMarkTransactionAsPaid(row);

      return (
        <PaymentHistoryActionControl
          isEnabled={isEnabled}
          onMarkAsPaid={() => handleOpenMarkAsPaidModal(row)}
          onViewDetails={() => handleOpenDetailsModal(row)}
        />
      );
    },
    [handleOpenMarkAsPaidModal, handleOpenDetailsModal],
  );

  const setExportReportName = useCallback((link: HTMLAnchorElement): void => {
    const currentDate = dateFormatMMDDYYYY(new Date());
    const fileName = `${PAYMENT_HISTORY_REPORT_NAME}_${currentDate}.xlsx`;
    link.setAttribute('download', fileName);
  }, []);

  const getExportQueryParams = useCallback((): ODataParams => {
    const parts: string[] = [];
    if (entitySearchAccount?.accountId != null) {
      parts.push(`AccountId eq ${entitySearchAccount.accountId}`);
    }

    if (selectedStatus) {
      parts.push(`PaymentStatus eq '${selectedStatus}'`);
    }

    const { startDate, endDate } = paymentDateRange;
    const rangeStart = startDate ?? endDate;
    const rangeEnd = endDate ?? startDate;
    if (rangeStart && rangeEnd) {
      const start = new Date(rangeStart);
      start.setHours(0, 0, 0, 0);
      const endInclusive = new Date(rangeEnd);
      endInclusive.setHours(23, 59, 59, 999);
      parts.push(
        `${BILLED_DATE_FILTER_FIELD} ge ${start.toISOString()} and ${BILLED_DATE_FILTER_FIELD} le ${endInclusive.toISOString()}`,
      );
    }

    return {
      reportName: PAYMENT_HISTORY_REPORT_NAME,
      ...(parts.length > 0 ? { $filter: parts.join(' and ') } : {}),
    };
  }, [entitySearchAccount?.accountId, paymentDateRange, selectedStatus]);

  const handleExportData = useCallback(async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const response = await downloadBreederPaymentHistoryReport({}, getExportQueryParams());
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setExportReportName(link);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      globalThis.URL.revokeObjectURL(url);
    } catch {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, getExportQueryParams, setExportReportName]);

  return (
    <>
      <MarkAsPaidModal
        isOpen={selectedRowForMarkAsPaid != null}
        outstandingBalanceCents={selectedRowForMarkAsPaid?.outstandingBalanceCents ?? 0}
        loading={markingAsPaid}
        onClose={closeMarkAsPaidModal}
        onConfirm={handleConfirmMarkAsPaid}
      />

      <BreederPaymentDetailsModal
        isOpen={selectedRowForDetails != null}
        breederPaymentTransactionId={selectedRowForDetails?.breederPaymentTransactionId}
        onClose={() => setSelectedRowForDetails(null)}
      />

      <div className="breeder-payments__filters payment-history__filters">
        <div className="breeder-payments__filter-group breeder-payments__filter-group--entity-search payment-history__search-filter-group">
          <label className="breeder-payments__filter-label">{BREEDER_PAYMENTS_CONSTANTS.SEARCH_BY}</label>
          <div className="breeder-payments__search-combined breeder-payments__search-combined--account-only">
            <div className="breeder-payments__search-combined-input">
              <AccountSearch
                key="payment-history-account-breeder"
                account={entitySearchAccount}
                onChange={setEntitySearchAccount}
                placeholder={BREEDER_PAYMENTS_CONSTANTS.SEARCH_BREEDER_PLACEHOLDER}
              />
            </div>
          </div>
        </div>
        <div className="breeder-payments__filter-group payment-history__date-filter-group">
          <label className="breeder-payments__filter-label">{BREEDER_PAYMENTS_CONSTANTS.DATE_RANGE}</label>
          <DateRangeInput
            value={paymentDateRange}
            onChange={setPaymentDateRange}
            isClearable
            maxDate={new Date()}
            rangeLimit={1}
          />
        </div>
        <div className="breeder-payments__filter-group payment-history__status-filter-group">
          <label className="breeder-payments__filter-label">{BREEDER_PAYMENTS_CONSTANTS.PAYMENT_STATUS}</label>
          <FilterDropdown
            options={PAYMENT_STATUS_OPTIONS}
            value={PAYMENT_STATUS_OPTIONS.find(opt => opt.value === selectedStatus) ?? PAYMENT_STATUS_OPTIONS[0]}
            onChange={e => setSelectedStatus(e.value)}
            placeholder="All Status"
            showSearch={true}
          />
        </div>

        <div className="breeder-payments__download-action">
          <button
            type="button"
            className={`button outlined download-report breeder-payments__download ${isExporting ? 'disabled' : ''}`}
            onClick={handleExportData}
            disabled={isExporting}>
            <DownloadSVG />
            &nbsp;{LABEL_CONSTANTS.DOWNLOAD}
          </button>
        </div>
      </div>

      <div className="breeder-payments__content">
        <div className="desk-breeder-payments payment-history__desktop-view">
          {accountId != null && (
            <PagedTable
              headers={HISTORY_PAGED_HEADERS}
              getData={getPaymentHistoryPaged}
              buildRow={(row: BreederPaymentHistoryRow) => {
                return [
                  row.transactionReferenceId,
                  row.breederName,
                  String(row.orders),
                  formatUsdFromCents(row.grossAmountCents),
                  formatUsdFromCents(row.commissionCents),
                  formatUsdFromCents(row.netPayableCents),
                  row.paidDate,
                  getPaymentStatusLabel(row),
                  renderActionControl(row),
                ];
              }}
              height={500}
              minHeight={156}
              pageSize={PAYMENT_HISTORY_PAGE_SIZE}
              scrollBufferThickness={100}
              refresh={tableRefresh}
              setRefresh={setTableRefresh}
              noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
            />
          )}
        </div>

        <div className="xs-breeder-payments payment-history__mobile-view">
          {!loading && rows.length === 0 ? (
            <div className="breeder-payments__no-results">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            rows.length > 0 &&
            rows.map(row => (
              <PaymentHistoryCard
                key={row.id}
                row={row}
                onOpenMarkAsPaid={handleOpenMarkAsPaidModal}
                onViewDetails={handleOpenDetailsModal}
              />
            ))
          )}
          {loading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>
    </>
  );
};

export default PaymentHistory;
